import React from "react";
import {observer} from "mobx-react-lite";
import {useNavigate} from "react-router-dom";

import {Button} from "primereact/button";
import {Tag} from "primereact/tag";
import {Knob} from "primereact/knob";

import appStore from "../../storage/AppStore";
import clubStore from "../../storage/ClubStore";
import systemStore from "../../storage/SystemStore";


const ROLE_SEVERITY = {
    MMBR: "info",
    PRNT: "success",
    MGR: "warning",
    TRN: "primary",
    MDC: "danger",
    SMM: "secondary",
};


const MemberList = ({
    members = [],
    role = "MGR",
}) => {

    const navigate = useNavigate();
    const getTeamNames = (member) => {
        if (
            !Array.isArray(member.teams) ||
            !Array.isArray(clubStore.teams)
        ) {
            return "";
        }
        return clubStore.teams
            .filter(
                (team) =>
                    member.teams.includes(
                        team.id
                    )
            )
            .map(
                (team) =>
                    team.name
            )
            .join(", ");
    };


    const archiveMember = async (member) => {
        if (
            !window.confirm(
                appStore.tr.are_you_sure_delete
            )
        ) {
            return;
        }

        await clubStore.archiveUser(
            member.id
        );

        systemStore.setSuccess(
            appStore.tr.successfully_saved
        );
    };


    const restoreMember = async (member) => {
        await clubStore.unArchiveUser(
            member.id
        );

        systemStore.setSuccess(
            appStore.tr.successfully_saved
        );
    };


    const openAttendance = (member) => {
        clubStore.attendance.query.setProperty(
            "page_number",
            1
        );

        clubStore.attendance.query.setProperty(
            "team_id",
            0
        );

        clubStore.attendance.query.setProperty(
            "company_user_id",
            member.id
        );

        clubStore.attendance.search();

        navigate(
            "/manager/trainings/attendance"
        );
    };


    const openBilling = (member) => {
        clubStore.billing.query.setProperty(
            "page_number",
            1
        );

        clubStore.billing.query.setProperty(
            "company_user_id",
            member.id
        );

        clubStore.billing.search();

        navigate(
            `/manager/member/${member.id}/billing`
        );
    };


    const MemberItem = ({member}) => {
        const teamNames = getTeamNames(member)
        const currency = clubStore.club?.country?.currency_sign || ""
        const awards = Array.isArray(member.awards) ? member.awards : []
    
        return (
          <div className={`secondary-card ${member.is_archived ? "opacity-60" : ""}`}>
              {/* TOP: avatar + basic info */}
              <div className="flex gap-3 align-items-start">
                  <div className="flex-shrink-0">
                      <img src={member.avatar} alt={member.title || ""} width={96} height={96}
                           className="border-circle" style={{objectFit: "cover"}}/>
                      {role === "TRN" && (
                        <div className="mt-2 flex justify-content-center">
                            <Knob value={member.rank || 0} size={54} readOnly valueTemplate="{value}%"/>
                        </div>
                      )}
                  </div>
              
                  <div className="flex-1 min-w-0">
                      <div className="text-lg font-semibold mb-2">{member.title}</div>
                  
                      <div className="flex flex-wrap gap-2">
                          {role === "MGR" && (
                            <Tag value={member.role?.name || member.role_id}
                                 severity={ROLE_SEVERITY[member.role_id] || "secondary"}/>
                          )}
                      
                          {member.role_id === "MMBR" && member.forma_number !== null &&
                            member.forma_number !== undefined && member.forma_number !== "" && (
                              <Tag value={`#${member.forma_number}`} severity="success"/>
                            )}
                      
                          {member.role_id === "MMBR" && member.year_born && (
                            <Tag value={member.year_born} severity="info"/>
                          )}
                      
                          {member.is_archived && (
                            <Tag value={appStore.tr.archive || "Архив"} severity="secondary" icon="pi pi-box"/>
                          )}
                      </div>
                  </div>
              </div>
          
              {/* AWARDS */}
              {awards.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                        {awards.map(award => (
                          <span key={award.award_type_id} className="flex align-items-center gap-2 text-sm">
                              <i className="pi pi-trophy text-yellow-500"/>
                              <>{`${award.name}${award.count > 1 ? ` ×${award.count}` : ""}`}</>
                          </span>
                        ))}
                </div>
              )}
          
              {/* CONTACTS */}
              <div className="flex flex-wrap gap-3 mt-3 text-sm text-600">
                  {member.phone_number ? (
                    <span className="flex align-items-center gap-2">
                        <i className="pi pi-phone"/>
                            {member.phone_number}
                      </span>
                  ) : member.role_id !== "MMBR" && (
                    <span className="flex align-items-center gap-2">
                    <i className="pi pi-exclamation-triangle"/>
                        {appStore.tr.no_phone_number}
                  </span>
                  )}
              
                  {member.user?.telegram_id ? (
                    <span className="flex align-items-center gap-2">
                    <i className="pi pi-telegram"/>
                        {member.user.username || "Telegram"}
                  </span>
                  ) : (
                    <span className="flex align-items-center gap-2">
                    <i className="pi pi-telegram"/>
                        {appStore.tr.no_telegram}
                  </span>
                  )}
              </div>
          
              {/* DETAILS */}
              <div className="flex flex-column gap-2 mt-3 text-sm">
                  {["TRN", "MMBR"].includes(member.role_id) && teamNames && (
                    <div className="flex align-items-start gap-2">
                        <i className="pi pi-users text-primary mt-1"/>
                        <span>{teamNames}</span>
                    </div>
                  )}
              
                  {member.role_id === "MMBR" && (member.mother_id > 0 || member.father_id > 0) && (
                    <div className="flex align-items-start gap-2">
                        <i className="pi pi-home text-primary mt-1"/>
                        <span>
                      {member.father_id > 0 && <>{appStore.tr.father}: <b>{member.father?.title}</b></>}
                            {member.father_id > 0 && member.mother_id > 0 && " · "}
                            {member.mother_id > 0 && <>{appStore.tr.mother}: <b>{member.mother?.title}</b></>}
                    </span>
                    </div>
                  )}
              
                  {member.role_id === "PRNT" && Array.isArray(member.children) && member.children.length > 0 && (
                    <div className="flex align-items-start gap-2">
                        <i className="pi pi-users text-primary mt-1"/>
                        <span>{appStore.tr.children}: <b>{member.children.map(child => child.title).join(", ")}</b></span>
                    </div>
                  )}
              
                  {role === "MGR" && member.role_id === "MMBR" &&
                    member.discount_amount !== null && member.discount_amount !== undefined && (
                      <div className="flex align-items-center gap-2">
                          <i className="pi pi-wallet text-primary"/>
                          <span>{appStore.tr.discount_amount || appStore.tr.discount}: <b>{member.discount_amount} {currency}</b></span>
                      </div>
                    )}
              </div>
          
              {/* ACTIONS */}
              {role === "MGR" && (
                <div className="flex flex-wrap gap-2 mt-3 pt-3 border-top-1 surface-border">
                    {member.is_archived ? (
                      <Button icon="pi pi-refresh" label={appStore.tr.restore || "Восстановить"} outlined
                              severity="info" size="small" onClick={() => restoreMember(member)}/>
                    ) : (
                      <>
                          <Button icon="pi pi-pencil" label={appStore.tr.edit} outlined severity="primary"
                                  size="small" onClick={() => navigate(`/manager/member/edit/${member.id}`)}/>
                      
                          {member.role_id === "MMBR" && (
                            <>
                                <Button icon="pi pi-chart-line" label={appStore.tr.attendance} outlined severity="info"
                                        size="small" onClick={() => openAttendance(member)}/>
                                <Button icon="pi pi-wallet" label={appStore.tr.billing} outlined severity="success"
                                        size="small" onClick={() => openBilling(member)}/>
                            </>
                          )}
                      
                          <Button icon="pi pi-trash" outlined severity="danger" size="small"
                                  tooltip={appStore.tr.delete} aria-label={appStore.tr.delete}
                                  onClick={() => archiveMember(member)}/>
                      </>
                    )}
                </div>
              )}
          </div>
        )
    }
    if (members.length === 0) {
        return (
            <div className="flex flex-column align-items-center justify-content-center py-6 text-600">

                <i
                    className="pi pi-users mb-3"
                    style={{
                        fontSize: "2rem",
                    }}
                />

                <div>
                    {appStore.tr.not_found}
                </div>

            </div>
        );
    }


    return (
        <>
            {members.map(
                (member) => (
                    <MemberItem
                        key={member.id}
                        member={member}
                    />
                )
            )}
        </>
    );
};


export default observer(
    MemberList
);